import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Location } from "history";
import { injectIntl, IntlShape } from "react-intl";
import { toast } from "react-toastify";
import { TableChangeParams } from "../../../components/Table/types";
import { GroundGraphqlContextStore } from "../../../lib/ground-aws-graphql-core";
import PageSubheader from "../../../components/PageSubheader";
import { EnumPaths } from "../../../utils/navigation";
import history from "../../../history";
import Table from "../../../components/Table";
import Dropdown, {
  DropdownValues,
} from "../../../components/Tailwind/Dropdown";
import images from "../../../images";
import Badge from "../../../components/Tailwind/Badge";
import { PaymentMethod } from "../../../lib/ground-aws-graphql-core/models/Api/PaymentMethods";
import { getTranslation } from "../../../utils/translation";
import ConfirmModal from "../../../utils/modal/confirm";
import IntlMessages from "../../../utils/messages";
import { DEFAULT_LIMIT, displayDayDDMMYYYY_HHMM } from "../../../utils/config";

interface Props {
  intl: IntlShape;
  location: Location<{
    pageIndex: number;
    limit: number;
    type: string;
  }>;
}

const ListPaymentMethods = ({ intl, location }: Props): JSX.Element => {
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [paymentMethodIdToDelete, setPaymentMethodIdToDelete] =
    useState<number>();
  const [initialParams, setInitialParams] = useState<TableChangeParams | null>(
    location?.state || null
  );

  const listPaymentMethods = GroundGraphqlContextStore.useStoreActions(
    a => a.paymentMethods.listPaymentMethods
  );
  const deletePaymentMethod = GroundGraphqlContextStore.useStoreActions(
    a => a.paymentMethods.deletePaymentMethod
  );
  const setPaymentMethod = GroundGraphqlContextStore.useStoreActions(
    a => a.paymentMethods.setPaymentMethod
  );

  const paymentMethods = GroundGraphqlContextStore.useStoreState(
    s => s.paymentMethods.paymentMethods.items
  );
  const total = GroundGraphqlContextStore.useStoreState(
    s => s.paymentMethods.paymentMethods.total
  );
  const centerTimezone = GroundGraphqlContextStore.useStoreState(
    s => s.center.centerTimezone
  );

  const fetchData = useCallback(
    (pageIndex = 0, limit = DEFAULT_LIMIT, showLoader = true) => {
      if (showLoader) setLoading(true);
      listPaymentMethods({ offset: pageIndex * limit, limit }).finally(() =>
        setLoading(false)
      );
    },
    [listPaymentMethods]
  );

  useEffect(() => {
    if (location?.state) {
      const { pageIndex, limit } = location.state;
      fetchData(pageIndex, limit);
    } else {
      fetchData();
    }
  }, [fetchData, location?.state]);

  const addNewPaymentMethod = () => {
    history.push(
      `/${EnumPaths.ROOT}/${EnumPaths.PAYMENT_METHODS}/${EnumPaths.CREATION_MODE}`
    );
  };

  const handleEditPaymentMethod = (id: number) => {
    setPaymentMethod(null);
    history.push(
      `/${EnumPaths.ROOT}/${EnumPaths.PAYMENT_METHODS}/${id}/${EnumPaths.EDIT_MODE}`
    );
  };

  const handleDelete = async () => {
    if (!paymentMethodIdToDelete) return;
    setLoading(true);
    setShowDeleteModal(false);

    try {
      await deletePaymentMethod({ id: paymentMethodIdToDelete });
      toast(intl.formatMessage({ id: "page.payment.method.delete.success" }), {
        type: "success",
      });
      fetchData(initialParams?.pageIndex, initialParams?.limit);
    } catch {
      toast(intl.formatMessage({ id: "page.payment.method.delete.error" }), {
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (params: TableChangeParams) => {
    setInitialParams(params);
    fetchData(params.pageIndex, params.limit, false);
  };

  const tableHead = useMemo(
    () => [
      "page.payment.method.label",
      "page.payment.method.code",
      "page.payment.method.enabled.in.bo",
      "general.status",
      "page.payment.method.created_at",
      "page.payment.method.updated_at",
      "general.actions",
    ],
    []
  );

  const tableBody = useMemo(
    () =>
      paymentMethods.map((pm: PaymentMethod) => {
        const {
          id,
          label,
          code,
          enabled,
          excluded_from_bo,
          created_at,
          updated_at,
        } = pm;

        const dropdownValues: DropdownValues[] = [
          {
            id: "edit",
            label: "page.list.payment.methods.edit",
            icon: images.edit,
            link: `/${EnumPaths.ROOT}/${EnumPaths.PAYMENT_METHODS}/${id}/${EnumPaths.EDIT_MODE}`,
          },
          {
            id: "delete",
            label: "general.delete",
            icon: images.deleteIcon,
            onClick: () => {
              setPaymentMethodIdToDelete(id);
              setShowDeleteModal(true);
            },
          },
        ];

        const rowElements = [
          {
            element: getTranslation(label),
            onCellClick: () => handleEditPaymentMethod(id),
          },
          { element: code, onCellClick: () => handleEditPaymentMethod(id) },
          {
            element: !excluded_from_bo ? (
              <Badge label="general.yes" bgClassName="bg-ground-green-100" />
            ) : (
              <Badge label="general.no" bgClassName="bg-red-500" />
            ),
            onCellClick: () => handleEditPaymentMethod(id),
          },
          {
            element: enabled ? (
              <Badge label="general.actif" bgClassName="bg-ground-green-100" />
            ) : (
              <Badge label="general.inactif" bgClassName="bg-red-500" />
            ),
            onCellClick: () => handleEditPaymentMethod(id),
          },
          {
            element: displayDayDDMMYYYY_HHMM(created_at, centerTimezone),
            onCellClick: () => handleEditPaymentMethod(id),
          },
          {
            element: displayDayDDMMYYYY_HHMM(updated_at, centerTimezone),
            onCellClick: () => handleEditPaymentMethod(id),
          },
          {
            element: (
              <Dropdown
                values={dropdownValues}
                dataCy={`payment-method-dropdown-${id}`}
              />
            ),
          },
        ];

        return { id: id.toString(), rowElements };
      }),
    [paymentMethods, centerTimezone]
  );

  return (
    <div>
      <Helmet>
        <title>
          {intl.formatMessage({
            id: "page.list.payment.methods.document.title",
          })}
        </title>
      </Helmet>

      <PageSubheader
        title="page.list.payment.methods.title"
        nbOfResults={total}
        buttonRightTitle="page.list.payment.methods.create.payment.method"
        buttonRightAction={addNewPaymentMethod}
        buttonRightId="btn-create-payment-method"
      />

      <Table
        initialParams={initialParams}
        head={tableHead}
        body={tableBody}
        noDataText="page.list.payment.methods.empty"
        onChange={handleTableChange}
        paginationLimit={DEFAULT_LIMIT}
        paginationTotal={total}
        loading={loading}
        setLoading={setLoading}
        className="px-8"
      />

      <ConfirmModal
        isOpen={showDeleteModal}
        toggle={() => setShowDeleteModal(prev => !prev)}
        onRequestClose={() => setShowDeleteModal(false)}
        handleConfirm={handleDelete}
        content={<IntlMessages id="page.list.payment.methods.delete" />}
      />
    </div>
  );
};

export default injectIntl(ListPaymentMethods);
